import React, { useEffect, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DateTime } from 'luxon';
import Avatar from './Avatar';
import {
  sendMatchRequest,
  acceptMatchRequest,
  declineMatchRequest,
  leaveCommunity
} from '../services/communityMatching';
import { useLanguage } from '../i18n';

const COMMUNITY_MSG_LIMIT = 30;

const PAYSTACK_PUBLIC_KEY = 'pk_live_ced368f17237c0a072b6db63d641ca539b9ca6be';
const MONTHLY_PLAN_CODE = 'PLN_fdt81n4wnr0z85q';
const SIX_MONTH_PLAN_CODE = 'PLN_prih2qg54fputl9';

// ─────────────────────────────────────────────────────────────
// Client-side mirror of the backend's isNewLocalDay()/normalizeTimezone()
// in functions/index.js. The backend only resets communityMessagesUsedToday
// (alongside groupMessagesUsedToday/groupVoiceNotesUsedToday, all reset
// together via lastGroupReset) lazily, on the first group/community send of
// a new day — so the user doc can sit at yesterday's count all day until
// someone actually sends something. Without this check, the UI displays
// that stale count as if it were still accurate. This does NOT touch
// Firestore — it only decides what to show, the same way the backend
// decides whether to reset before checking the limit.
// ─────────────────────────────────────────────────────────────
function normalizeTimezone(tz) {
  if (!tz || typeof tz !== 'string') return 'UTC';
  if (DateTime.now().setZone(tz).isValid) return tz;
  const match = tz.match(/^GMT([+-]\d{1,2}(:\d{2})?)$/i);
  if (match) {
    const converted = `UTC${match[1]}`;
    if (DateTime.now().setZone(converted).isValid) return converted;
  }
  return 'UTC';
}

function isNewLocalDay(lastReset, timezone) {
  const zone = normalizeTimezone(timezone);
  if (!lastReset) return true;
  const lastDate = lastReset.toDate ? lastReset.toDate() : new Date(lastReset);
  const last = DateTime.fromJSDate(lastDate, { zone });
  const now = DateTime.now().setZone(zone);
  return last.toISODate() !== now.toISODate();
}

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes messageIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .comm-input:focus {
    border-color: #008081 !important;
    box-shadow: 0 0 0 3px rgba(0,128,129,0.15) !important;
    outline: none !important;
  }
  .comm-send-btn {
    transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s !important;
  }
  .comm-send-btn:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(0,128,129,0.4) !important;
  }
  .comm-header-btn {
    transition: background-color 0.2s, border-color 0.2s !important;
  }
  .comm-header-btn:hover {
    background-color: rgba(0,128,129,0.08) !important;
    border-color: rgba(0,128,129,0.3) !important;
  }
  .comm-request-card {
    transition: border-color 0.2s !important;
  }
  .comm-request-card:hover {
    border-color: rgba(0,128,129,0.3) !important;
  }
  .comm-match-btn {
    transition: background-color 0.2s, border-color 0.2s, opacity 0.2s !important;
  }
  .comm-match-btn:hover:not(:disabled) {
    background-color: rgba(0,128,129,0.12) !important;
    border-color: rgba(0,128,129,0.4) !important;
  }
  .premium-plan-btn {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .premium-plan-btn:hover:not(:disabled) {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(0,0,0,0.3) !important;
  }
  .strike-modal-btn {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .strike-modal-btn:hover {
    transform: translateY(-1px) !important;
  }
`;

const wrapperAnim = {
  animation: 'fadeInUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)'
};

// ============================================
// PREMIUM MODAL - Paystack inline checkout
// ============================================
function PremiumModal({ onClose, userEmail, userId, theme }) {
  const isDark = theme === 'dark';
  const [paystackLoaded, setPaystackLoaded] = useState(false);

  useEffect(() => {
    if (window.PaystackPop) { setPaystackLoaded(true); return; }
    const existing = document.getElementById('paystack-inline-script');
    if (existing) {
      existing.addEventListener('load', () => setPaystackLoaded(true));
      return;
    }
    const script = document.createElement('script');
    script.id = 'paystack-inline-script';
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setPaystackLoaded(true);
    document.body.appendChild(script);
  }, []);

  // Theme-aware colors for modal
  const modalColors = {
    bg: isDark 
      ? 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))'
      : 'linear-gradient(145deg, #ffffff, #f8f9fa)',
    border: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    text: isDark ? '#ffffff' : '#1a1a2e',
    textSecondary: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
    featureBg: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    featureBorder: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    featureText: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.8)',
    closeBtnBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)',
    closeBtnText: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
  };

  const handleUpgrade = (planCode, planName) => {
    if (!paystackLoaded || !window.PaystackPop) {
      alert('Payment system is loading, please try again in a moment.');
      return;
    }
    const amount = planCode === MONTHLY_PLAN_CODE ? 64500 : 258000;
    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: userEmail,
      amount: amount,
      currency: 'KES',
      plan: planCode,
      metadata: {
        custom_fields: [
          { display_name: 'User ID', variable_name: 'userId', value: userId },
          { display_name: 'Plan Name', variable_name: 'planName', value: planName }
        ]
      },
      callback: function(response) {
        console.log('Payment successful:', response.reference);
      },
      onClose: function() {}
    });
    handler.openIframe();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem',
      animation: 'fadeInUp 0.25s ease'
    }}>
      <div style={{
        background: modalColors.bg,
        backdropFilter: 'blur(20px)',
        borderRadius: '1.5rem', padding: '2rem',
        maxWidth: '380px', width: '100%',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
        border: `1px solid ${modalColors.border}`
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div style={{
            width: '64px', height: '64px', margin: '0 auto 1rem',
            background: 'linear-gradient(135deg, #008081, #4B2C5E)',
            borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '1.75rem',
            boxShadow: '0 8px 24px rgba(0,128,129,0.35)'
          }}>✨</div>
          <h2 style={{ margin: 0, color: modalColors.text, fontSize: '1.35rem', fontWeight: '800', letterSpacing: '-0.02em' }}>
            Upgrade to Premium
          </h2>
          <p style={{ margin: '0.5rem 0 0', color: modalColors.textSecondary, fontSize: '0.875rem', lineHeight: 1.6 }}>
            You've reached your daily community message limit.
          </p>
        </div>
        <div style={{
          backgroundColor: modalColors.featureBg,
          borderRadius: '1rem', padding: '0.25rem 0',
          marginBottom: '1.5rem',
          border: `1px solid ${modalColors.featureBorder}`
        }}>
          {[
            { icon: '✉️', label: 'Unlimited messages' },
            { icon: '🎤', label: 'Unlimited voice notes' },
            { icon: '👥', label: 'Unlimited group messages' },
            { icon: '🌍', label: 'Unlimited community messages' },
          ].map((feature, i, arr) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.75rem 1rem',
              color: modalColors.featureText, fontSize: '0.875rem',
              borderBottom: i < arr.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` : 'none'
            }}>
              <span style={{ fontSize: '1.1rem' }}>{feature.icon}</span>
              {feature.label}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <button className="premium-plan-btn" onClick={() => handleUpgrade(MONTHLY_PLAN_CODE, 'monthly')} disabled={!paystackLoaded}
            style={{ padding: '1rem', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#FFFFFF', border: 'none', borderRadius: '0.875rem', fontWeight: '700', cursor: paystackLoaded ? 'pointer' : 'not-allowed', fontSize: '0.95rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 15px rgba(0,128,129,0.35)', opacity: paystackLoaded ? 1 : 0.7 }}>
            <span>Monthly</span>
            <span style={{ fontSize: '1.1rem' }}>$5 / month</span>
          </button>
          <button className="premium-plan-btn" onClick={() => handleUpgrade(SIX_MONTH_PLAN_CODE, 'sixMonth')} disabled={!paystackLoaded}
            style={{ padding: '1rem', background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)', color: '#FFFFFF', border: 'none', borderRadius: '0.875rem', fontWeight: '700', cursor: paystackLoaded ? 'pointer' : 'not-allowed', fontSize: '0.95rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 15px rgba(75,44,94,0.35)', opacity: paystackLoaded ? 1 : 0.7 }}>
            <span>6 Months <span style={{ fontSize: '0.75rem', opacity: 0.75, marginLeft: '0.35rem' }}>Save 33%</span></span>
            <span style={{ fontSize: '1.1rem' }}>$20 total</span>
          </button>
        </div>
        <button onClick={onClose} style={{ 
          width: '100%', padding: '0.75rem', 
          backgroundColor: 'transparent', 
          border: `1px solid ${modalColors.closeBtnBorder}`, 
          borderRadius: '0.875rem', 
          color: modalColors.closeBtnText, 
          cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' 
        }}>
          Maybe later
        </button>
      </div>
    </div>
  );
}

// ============================================
// STRIKE / BAN WARNING MODAL
// Shown when a message is blocked by the server-side content filter.
// Theme-aware to match this component's light/dark support.
// ============================================
function StrikeWarningModal({ strikes, banned, theme, onClose }) {
  const isDark = theme === 'dark';
  const textColor      = isDark ? '#ffffff' : '#1a1a2e';
  const textSecondary  = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
  const bgColor        = isDark
    ? 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))'
    : 'linear-gradient(145deg, #ffffff, #f8f9fa)';
  const trackBg        = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem',
      animation: 'fadeInUp 0.25s ease'
    }}>
      <div style={{
        background: bgColor,
        backdropFilter: 'blur(20px)',
        borderRadius: '1.5rem',
        padding: '2rem',
        maxWidth: '380px',
        width: '100%',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
        border: banned ? '1px solid rgba(224,92,92,0.3)' : '1px solid rgba(250,204,21,0.25)',
        textAlign: 'center'
      }}>
        <div style={{
          width: '64px', height: '64px', margin: '0 auto 1.25rem',
          background: banned ? 'rgba(224,92,92,0.15)' : 'rgba(250,204,21,0.12)',
          border: banned ? '1px solid rgba(224,92,92,0.3)' : '1px solid rgba(250,204,21,0.25)',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.75rem'
        }}>
          {banned ? '🔒' : '💛'}
        </div>

        <h2 style={{ margin: 0, color: textColor, fontSize: '1.25rem', fontWeight: '800', letterSpacing: '-0.02em' }}>
          {banned ? 'Account disabled' : "Let's keep this space safe"}
        </h2>

        <p style={{ margin: '0.75rem 0 0', color: textSecondary, fontSize: '0.9rem', lineHeight: 1.7 }}>
          {banned
            ? "Your account has been permanently disabled after repeated messages that didn't meet Veyl's community guidelines."
            : "That message didn't go through — it didn't feel like it fit the space this community is building together. Take a breath, and feel free to try rephrasing."}
        </p>

        {!banned && (
          <div style={{
            marginTop: '1.25rem',
            display: 'flex', justifyContent: 'center', gap: '0.5rem'
          }}>
            {[1, 2, 3].map(n => (
              <div key={n} style={{
                width: '32px', height: '6px', borderRadius: '3px',
                backgroundColor: n <= strikes ? '#facc15' : trackBg
              }} />
            ))}
          </div>
        )}

        <button
          className="strike-modal-btn"
          onClick={onClose}
          style={{
            marginTop: '1.5rem', width: '100%', padding: '0.875rem',
            background: banned ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'linear-gradient(135deg, #008081, #005f5f)',
            color: banned ? textColor : '#ffffff',
            border: banned ? `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}` : 'none',
            borderRadius: '0.875rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem',
            boxShadow: banned ? 'none' : '0 4px 15px rgba(0,128,129,0.35)'
          }}
        >
          {banned ? 'Close' : 'Got it'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMMUNITY VIEW COMPONENT
// ============================================
export default function CommunityView({ user, userData, setUserData, theme, t: themeTokens, translate: t }) {
  const dark = theme === 'dark';

  // Theme-aware colors
  const colors = {
    cardBg:           dark ? 'rgba(255,255,255,0.02)' : '#FFFFFF',
    cardBorder:       dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    headerBg:         dark ? 'rgba(255,255,255,0.02)' : '#f9fafb',
    headerBorder:     dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    msgAreaBg:        dark ? 'rgba(0,0,0,0.15)' : '#f3f4f6',
    inputAreaBg:      dark ? 'rgba(255,255,255,0.02)' : '#f9fafb',
    inputBg:          dark ? 'rgba(255,255,255,0.05)' : '#ffffff',
    inputBorder:      dark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
    inputColor:       dark ? '#ffffff' : '#1B2D44',
    text:             dark ? '#ffffff' : '#1B2D44',
    subtext:          dark ? 'rgba(255,255,255,0.4)' : '#6b7280',
    mutedText:        dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    emptyText:        dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    partnerMsgBg:     dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    partnerMsgBorder: dark ? 'rgba(255,255,255,0.08)' : '#d1d5db',
    partnerMsgText:   dark ? '#ffffff' : '#1B2D44',
    senderName:       dark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
    deletingText:     dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    btnBg:            dark ? 'rgba(255,255,255,0.05)' : '#f3f4f6',
    btnBorder:        dark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
    btnText:          dark ? 'rgba(255,255,255,0.6)' : '#4b5563',
    reqCardBg:        dark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    reqCardBorder:    dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    reqNameText:      dark ? '#ffffff' : '#1B2D44',
    reqSubText:       dark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
    reqOutText:       dark ? 'rgba(255,255,255,0.6)' : '#6b7280',
    sectionLabel:     dark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
    noPendingText:    dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    backBtn:          dark ? 'rgba(255,255,255,0.4)' : '#6b7280',
    matchBtnBorder:   dark ? 'rgba(255,255,255,0.12)' : '#d1d5db',
    matchBtnText:     dark ? 'rgba(255,255,255,0.5)' : '#6b7280',
    leaveStayBorder:  dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
    leaveStayText:    dark ? 'rgba(255,255,255,0.6)' : '#6b7280',
    leaveDescText:    dark ? 'rgba(255,255,255,0.45)' : '#6b7280',
  };

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [showRequests, setShowRequests] = useState(false);
  const [requestingUser, setRequestingUser] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [hoveredMsgId, setHoveredMsgId] = useState(null);
  const [deletingMsgId, setDeletingMsgId] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [strikeWarning, setStrikeWarning] = useState(null); // { strikes, banned } | null
  const messagesEndRef = useRef(null);

  const communityId = userData?.communityId;
  const isPremium = userData?.isPremium;

  // Mirrors the backend's lazy-reset check (functions/index.js, isNewLocalDay).
  // Community counters reset together with group counters via lastGroupReset —
  // see sendCommunityMessage / sendGroupMessage in functions/index.js.
  const communityTimezone = userData?.timezone || 'UTC';
  const lastGroupReset = userData?.lastGroupReset;
  const communityCountersStale = isNewLocalDay(lastGroupReset, communityTimezone);

  const communityMsgsUsed = communityCountersStale ? 0 : (userData?.communityMessagesUsedToday || 0);
  const communityMsgsLeft = isPremium ? 999 : (COMMUNITY_MSG_LIMIT - communityMsgsUsed);
  const myAvatarId = userData?.avatarId || null;
  const activeVeylId = userData?.activeVeylId;
  const pendingOutgoing = outgoingRequests.filter(r => r.status === 'pending').length;
  const canSendRequest = pendingOutgoing < 3;

  // Listen to community messages
  useEffect(() => {
    if (!communityId) return;
    const q = query(collection(db, 'communities', communityId, 'messages'), orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = [];
      snapshot.forEach(d => msgs.push({ id: d.id, ...d.data() }));
      setMessages(msgs);
    });
    return () => unsubscribe();
  }, [communityId]);

  // Listen to incoming match requests
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'matchRequests'), where('toUserId', '==', user.uid), where('status', '==', 'pending'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reqs = [];
      snapshot.forEach(d => reqs.push({ id: d.id, ...d.data() }));
      setIncomingRequests(reqs);
    });
    return () => unsubscribe();
  }, [user]);

  // Listen to outgoing match requests
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'matchRequests'), where('fromUserId', '==', user.uid), where('status', '==', 'pending'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reqs = [];
      snapshot.forEach(d => reqs.push({ id: d.id, ...d.data() }));
      setOutgoingRequests(reqs);
    });
    return () => unsubscribe();
  }, [user]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  
  const sendMessage = async (e) => {
  e.preventDefault();
  if (!newMessage.trim() || loading || !communityId) return;
  if (!isPremium && communityMsgsLeft <= 0) {
    setShowPremiumModal(true);
    return;
  }
  setLoading(true);
  try {
    const functions = getFunctions();
    const sendCommunityMessage = httpsCallable(functions, 'sendCommunityMessage');
    await sendCommunityMessage({ communityId, text: newMessage });
    setNewMessage('');
  } catch (err) {
    if (err.code === 'functions/resource-exhausted') {
      setShowPremiumModal(true);
    } else if (err.code === 'functions/failed-precondition' && err.details?.moderationBlocked) {
      setStrikeWarning({ strikes: err.details.strikes, banned: err.details.banned });
    } else {
      alert(t('error') + ': ' + err.message);
    }
  } finally {
    setLoading(false);
  }
}; 

  const deleteMessage = async (msgId) => {
    if (!communityId) return;
    setDeletingMsgId(msgId);
    try {
      await deleteDoc(doc(db, 'communities', communityId, 'messages', msgId));
    } catch (err) {
      alert(t('error') + ': ' + err.message);
    } finally {
      setDeletingMsgId(null);
    }
  };

  const handleSendMatchRequest = async (msg) => {
    if (!canSendRequest) { 
      alert(t('matchRequestLimit')); 
      return; 
    }
    if (msg.senderId === user.uid) return;
    setRequestingUser(msg.senderId);
    const result = await sendMatchRequest(
      user.uid, 
      activeVeylId, 
      userData.name, 
      myAvatarId, 
      msg.senderId, 
      msg.veylId, 
      userData.goalCategory
    );
    setRequestingUser(null);
    if (result.success) alert(t('matchRequestSent'));
    else if (result.reason === 'limit') alert(t('matchRequestLimit'));
    else if (result.reason === 'duplicate') alert(t('matchRequestDuplicate'));
    else alert(t('matchRequestError'));
  };

  const handleAccept = async (request) => {
    const success = await acceptMatchRequest(request.id, request, user.uid);
    if (success) {
      setUserData(prev => ({
        ...prev, 
        communityId: null,
        activeVeyl: { 
          ...prev.activeVeyl, 
          partnerId: request.fromUserId, 
          partnerVeylId: request.fromVeylId, 
          status: 'matched' 
        }
      }));
    } else {
      alert(t('matchAcceptError'));
    }
  };

  const handleDecline = async (request) => {
    await declineMatchRequest(request.id, request, user.uid);
  };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      await leaveCommunity(user.uid);
      setUserData(prev => ({ ...prev, communityId: null }));
      setShowLeaveConfirm(false);
    } catch (err) {
      alert(t('error') + ': ' + err.message);
    } finally {
      setLeaving(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // ============================================
  // RENDER: No Community (Join Screen)
  // ============================================
  if (!communityId) {
    return (
      <div style={{ maxWidth: '42rem', margin: '0 auto', ...wrapperAnim }}>
        <style>{styleTag}</style>
        <div style={{
          background: colors.cardBg, 
          backdropFilter: 'blur(10px)',
          borderRadius: '1.25rem', 
          padding: '3rem 2rem',
          border: `1px solid ${colors.cardBorder}`, 
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌐</div>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '800', 
            marginBottom: '0.75rem', 
            color: colors.text, 
            letterSpacing: '-0.02em' 
          }}>
            {t('veylCommunity')}
          </h2>
          <p style={{ color: colors.subtext, fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1rem' }}>
            {t('communityDesc')}
          </p>
          <p style={{ color: colors.mutedText, fontSize: '0.8rem', margin: 0 }}>
            {t('communityJoinNote')}
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Leave Confirmation
  // ============================================
  if (showLeaveConfirm) {
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto', ...wrapperAnim }}>
        <style>{styleTag}</style>
        <div style={{
          background: colors.cardBg, 
          backdropFilter: 'blur(10px)',
          borderRadius: '1.25rem', 
          padding: '2.5rem 2rem',
          border: `1px solid ${colors.cardBorder}`, 
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px', 
            height: '64px', 
            margin: '0 auto 1.25rem',
            backgroundColor: 'rgba(224,92,92,0.1)',
            border: '1px solid rgba(224,92,92,0.25)',
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center', 
            fontSize: '1.75rem'
          }}>👋</div>
          <h2 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '800', 
            marginBottom: '0.75rem', 
            color: colors.text 
          }}>
            {t('leaveCommunity')}
          </h2>
          <p style={{ 
            color: colors.leaveDescText, 
            fontSize: '0.875rem', 
            lineHeight: 1.7, 
            marginBottom: '1.75rem' 
          }}>
            {t('communityLeaveDesc')}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowLeaveConfirm(false)}
              style={{ 
                flex: 1, 
                padding: '0.875rem', 
                border: `1px solid ${colors.leaveStayBorder}`, 
                backgroundColor: 'transparent', 
                color: colors.leaveStayText, 
                borderRadius: '0.875rem', 
                fontWeight: '600', 
                cursor: 'pointer' 
              }}
            >
              {t('stay')}
            </button>
            <button
              onClick={handleLeave}
              disabled={leaving}
              style={{ 
                flex: 1, 
                padding: '0.875rem', 
                backgroundColor: '#E05C5C', 
                color: '#FFFFFF', 
                border: 'none', 
                borderRadius: '0.875rem', 
                fontWeight: '700', 
                cursor: leaving ? 'not-allowed' : 'pointer', 
                opacity: leaving ? 0.6 : 1, 
                boxShadow: '0 4px 15px rgba(224,92,92,0.3)' 
              }}
            >
              {leaving ? t('leaving') : t('leave')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Match Requests View
  // ============================================
  if (showRequests) {
    return (
      <div style={{ maxWidth: '42rem', margin: '0 auto', ...wrapperAnim }}>
        <style>{styleTag}</style>
        <div style={{
          background: colors.cardBg, 
          backdropFilter: 'blur(10px)',
          borderRadius: '1.25rem', 
          padding: '1.75rem',
          border: `1px solid ${colors.cardBorder}`
        }}>
          <button
            onClick={() => setShowRequests(false)}
            style={{ 
              marginBottom: '1.25rem', 
              color: colors.backBtn, 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              fontSize: '0.875rem', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.35rem' 
            }}
          >
            ← {t('backToChat')}
          </button>
          <h2 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '800', 
            marginBottom: '1.5rem', 
            color: colors.text, 
            letterSpacing: '-0.02em' 
          }}>
            {t('matchRequests')}
          </h2>

          {/* Incoming Requests */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '0.72rem', 
              fontWeight: '700', 
              color: colors.sectionLabel, 
              marginBottom: '1rem', 
              textTransform: 'uppercase', 
              letterSpacing: '0.1em' 
            }}>
              {t('incoming')} ({incomingRequests.length})
            </h3>
            {incomingRequests.length === 0 ? (
              <p style={{ color: colors.noPendingText, fontSize: '0.875rem' }}>
                {t('noIncoming')}
              </p>
            ) : (
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {incomingRequests.map(req => (
                  <div 
                    key={req.id} 
                    className="comm-request-card" 
                    style={{ 
                      padding: '1rem', 
                      border: `1px solid ${colors.reqCardBorder}`, 
                      borderRadius: '0.875rem', 
                      backgroundColor: colors.reqCardBg 
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center' 
                    }}>
                      <div>
                        <div style={{ 
                          color: colors.reqNameText, 
                          fontWeight: '600', 
                          fontSize: '0.9rem' 
                        }}>
                          {req.fromName}
                        </div>
                        <div style={{ 
                          color: colors.reqSubText, 
                          fontSize: '0.75rem', 
                          marginTop: '0.1rem' 
                        }}>
                          {t('requestToAnonymous')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                          onClick={() => handleDecline(req)} 
                          style={{ 
                            padding: '0.5rem 0.875rem', 
                            border: `1px solid ${colors.btnBorder}`, 
                            backgroundColor: 'transparent', 
                            color: colors.btnText, 
                            borderRadius: '0.5rem', 
                            fontWeight: '600', 
                            cursor: 'pointer', 
                            fontSize: '0.8rem' 
                          }}
                        >
                          {t('decline')}
                        </button>
                        <button 
                          onClick={() => handleAccept(req)} 
                          style={{ 
                            padding: '0.5rem 0.875rem', 
                            background: 'linear-gradient(135deg, #008081, #005f5f)', 
                            color: '#FFFFFF', 
                            border: 'none', 
                            borderRadius: '0.5rem', 
                            fontWeight: '700', 
                            cursor: 'pointer', 
                            fontSize: '0.8rem', 
                            boxShadow: '0 2px 8px rgba(0,128,129,0.3)' 
                          }}
                        >
                          ✅ {t('accept')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Outgoing Requests */}
          <div>
            <h3 style={{ 
              fontSize: '0.72rem', 
              fontWeight: '700', 
              color: colors.sectionLabel, 
              marginBottom: '1rem', 
              textTransform: 'uppercase', 
              letterSpacing: '0.1em' 
            }}>
              {t('sent')} ({pendingOutgoing}/3)
            </h3>
            {outgoingRequests.length === 0 ? (
              <p style={{ color: colors.noPendingText, fontSize: '0.875rem' }}>
                {t('noPending')}
              </p>
            ) : (
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {outgoingRequests.map(req => (
                  <div 
                    key={req.id} 
                    style={{ 
                      padding: '1rem', 
                      border: `1px solid ${colors.reqCardBorder}`, 
                      borderRadius: '0.875rem', 
                      backgroundColor: colors.reqCardBg, 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center' 
                    }}
                  >
                    <div style={{ fontSize: '0.875rem', color: colors.reqOutText }}>
                      {t('requestToAnonymous')}
                    </div>
                    <span style={{ 
                      fontSize: '0.72rem', 
                      color: '#facc15', 
                      fontWeight: '700', 
                      backgroundColor: 'rgba(250,204,21,0.1)', 
                      padding: '0.2rem 0.6rem', 
                      borderRadius: '9999px' 
                    }}>
                      {t('pending')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Main Chat View
  // ============================================
  return (
    <>
      <style>{styleTag}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', ...wrapperAnim }}>

        {/* Incoming request banner */}
        {incomingRequests.length > 0 && (
          <div style={{ 
            padding: '1rem 1.25rem', 
            background: 'rgba(0,128,129,0.1)', 
            border: '1px solid rgba(0,128,129,0.25)', 
            borderRadius: '0.875rem', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <div style={{ fontSize: '0.875rem', color: colors.text }}>
              🤝 <strong>{incomingRequests.length}</strong> {t('requestWaiting', { count: incomingRequests.length, plural: incomingRequests.length !== 1 ? 's' : '' })}
            </div>
            <button 
              onClick={() => setShowRequests(true)} 
              style={{ 
                padding: '0.4rem 0.875rem', 
                background: 'linear-gradient(135deg, #008081, #005f5f)', 
                color: '#FFFFFF', 
                border: 'none', 
                borderRadius: '0.5rem', 
                fontWeight: '700', 
                cursor: 'pointer', 
                fontSize: '0.8rem', 
                boxShadow: '0 2px 8px rgba(0,128,129,0.3)' 
              }}
            >
              {t('view')}
            </button>
          </div>
        )}

        {/* Main chat container */}
        <div style={{
          background: colors.cardBg, 
          backdropFilter: 'blur(10px)',
          borderRadius: '1.25rem', 
          border: `1px solid ${colors.cardBorder}`,
          display: 'flex', 
          flexDirection: 'column',
          height: '600px', 
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{ 
            padding: '1rem 1.25rem', 
            borderBottom: `1px solid ${colors.headerBorder}`, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            background: colors.headerBg 
          }}>
            <div>
              <div style={{ 
                fontWeight: '700', 
                color: colors.text, 
                fontSize: '0.95rem' 
              }}>
                {userData?.goalCategory} {t('community')}
              </div>
              <div style={{ 
                fontSize: '0.78rem', 
                color: colors.subtext, 
                marginTop: '0.15rem', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.35rem', 
                flexWrap: 'wrap' 
              }}>
                <span>{t('openToAll')}</span>
                {!isPremium && (
                  <>
                    <span style={{ 
                      color: communityMsgsLeft <= 5 ? '#f87171' : colors.mutedText 
                    }}>
                      · {communityMsgsLeft} msgs left
                    </span>
                    <span 
                      onClick={() => setShowPremiumModal(true)} 
                      style={{ 
                        color: '#008081', 
                        cursor: 'pointer', 
                        fontWeight: '600' 
                      }}
                    >
                      · ✨ Upgrade
                    </span>
                  </>
                )}
                {isPremium && (
                  <span style={{ color: '#c084fc', fontWeight: '600' }}>
                    · ✨ Premium
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="comm-header-btn" 
                onClick={() => setShowRequests(true)}
                style={{ 
                  background: colors.btnBg, 
                  border: `1px solid ${colors.btnBorder}`, 
                  borderRadius: '0.5rem', 
                  padding: '0.4rem 0.75rem', 
                  cursor: 'pointer', 
                  color: colors.btnText, 
                  fontSize: '0.78rem', 
                  fontWeight: '500', 
                  position: 'relative' 
                }}
              >
                {t('requestsBtn')}
                {(incomingRequests.length > 0 || pendingOutgoing > 0) && (
                  <span style={{ 
                    position: 'absolute', 
                    top: '-4px', 
                    right: '-4px', 
                    width: '7px', 
                    height: '7px', 
                    borderRadius: '50%', 
                    backgroundColor: '#E05C5C' 
                  }} />
                )}
              </button>
              <button 
                className="comm-header-btn" 
                onClick={() => setShowLeaveConfirm(true)}
                style={{ 
                  background: colors.btnBg, 
                  border: `1px solid ${colors.btnBorder}`, 
                  borderRadius: '0.5rem', 
                  padding: '0.4rem 0.75rem', 
                  cursor: 'pointer', 
                  color: colors.btnText, 
                  fontSize: '0.78rem', 
                  fontWeight: '500' 
                }}
              >
                {t('leaveBtn')}
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            padding: '1.25rem', 
            background: colors.msgAreaBg 
          }}>
            {messages.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                color: colors.emptyText, 
                paddingTop: '3rem' 
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🌐</div>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  {t('noCommunityMessages')}
                </p>
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.senderId === user.uid;
                const isHovered = hoveredMsgId === msg.id;
                const isDeleting = deletingMsgId === msg.id;
                return (
                  <div 
                    key={msg.id} 
                    onMouseEnter={() => isMe && setHoveredMsgId(msg.id)} 
                    onMouseLeave={() => setHoveredMsgId(null)}
                    style={{ 
                      display: 'flex', 
                      justifyContent: isMe ? 'flex-end' : 'flex-start', 
                      marginBottom: '1rem', 
                      gap: '0.5rem', 
                      alignItems: 'flex-end', 
                      animation: 'messageIn 0.2s ease' 
                    }}
                  >
                    {!isMe && (
                      <Avatar 
                        username={msg.senderName} 
                        avatarId={msg.senderAvatarId} 
                        size={30} 
                      />
                    )}
                    <div style={{ 
                      maxWidth: '70%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: isMe ? 'flex-end' : 'flex-start' 
                    }}>
                      {!isMe && (
                        <div style={{ 
                          fontSize: '0.68rem', 
                          color: colors.senderName, 
                          marginBottom: '0.25rem', 
                          paddingLeft: '0.25rem' 
                        }}>
                          {msg.senderName}
                        </div>
                      )}
                      <div style={{
                        padding: '0.75rem 1rem',
                        borderRadius: isMe ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                        background: isMe ? 'linear-gradient(135deg, #008081, #005f5f)' : colors.partnerMsgBg,
                        color: isMe ? '#ffffff' : colors.partnerMsgText,
                        border: isMe ? 'none' : `1px solid ${colors.partnerMsgBorder}`,
                        opacity: isDeleting ? 0.4 : 1, 
                        transition: 'opacity 0.2s',
                        boxShadow: isMe ? '0 4px 12px rgba(0,128,129,0.25)' : 'none'
                      }}>
                        <span style={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
                          {msg.text}
                        </span>
                        <div style={{ 
                          fontSize: '0.68rem', 
                          opacity: 0.5, 
                          marginTop: '0.3rem', 
                          textAlign: isMe ? 'right' : 'left' 
                        }}>
                          {formatTime(msg.timestamp)}
                        </div>
                      </div>
                      {isMe && isHovered && !isDeleting && (
                        <button 
                          onClick={() => deleteMessage(msg.id)} 
                          style={{ 
                            marginTop: '0.25rem', 
                            padding: '0.15rem 0.5rem', 
                            backgroundColor: 'transparent', 
                            border: 'none', 
                            borderRadius: '999px', 
                            cursor: 'pointer', 
                            color: '#E05C5C', 
                            fontSize: '0.7rem', 
                            fontWeight: '500', 
                            opacity: 0.8 
                          }}
                        >
                          {t('deleteMessage')}
                        </button>
                      )}
                      {isDeleting && (
                        <span style={{ 
                          fontSize: '0.7rem', 
                          color: colors.deletingText, 
                          marginTop: '0.2rem' 
                        }}>
                          {t('deleting')}
                        </span>
                      )}
                      {!isMe && msg.veylId && (
                        <button 
                          className="comm-match-btn" 
                          onClick={() => handleSendMatchRequest(msg)}
                          disabled={requestingUser === msg.senderId || !canSendRequest}
                          style={{ 
                            marginTop: '0.35rem', 
                            padding: '0.3rem 0.875rem', 
                            backgroundColor: 'transparent', 
                            border: `1px solid ${colors.matchBtnBorder}`, 
                            borderRadius: '9999px', 
                            cursor: (!canSendRequest || requestingUser === msg.senderId) ? 'not-allowed' : 'pointer', 
                            color: colors.matchBtnText, 
                            fontSize: '0.72rem', 
                            fontWeight: '500', 
                            opacity: (!canSendRequest || requestingUser === msg.senderId) ? 0.4 : 1 
                          }}
                        >
                          {requestingUser === msg.senderId ? t('requestSending') : t('requestMatch')}
                        </button>
                      )}
                    </div>
                    {isMe && (
                      <Avatar 
                        username={userData.name} 
                        avatarId={myAvatarId} 
                        size={30} 
                      />
                    )}
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Low limit warning */}
          {!isPremium && communityMsgsLeft <= 5 && communityMsgsLeft > 0 && (
            <div style={{ 
              padding: '0.5rem 1.25rem', 
              background: 'rgba(250,204,21,0.06)', 
              borderTop: '1px solid rgba(250,204,21,0.15)' 
            }}>
              <p style={{ 
                fontSize: '0.75rem', 
                color: '#facc15', 
                margin: 0, 
                textAlign: 'center' 
              }}>
                {communityMsgsLeft} messages left today.{' '}
                <span 
                  onClick={() => setShowPremiumModal(true)} 
                  style={{ 
                    textDecoration: 'underline', 
                    cursor: 'pointer', 
                    fontWeight: '600' 
                  }}
                >
                  Upgrade
                </span>{' '}
                for unlimited.
              </p>
            </div>
          )}

          {/* Limit reached */}
          {!isPremium && communityMsgsLeft <= 0 && (
            <div style={{ 
              padding: '0.75rem 1.25rem', 
              borderTop: `1px solid ${colors.cardBorder}`, 
              textAlign: 'center' 
            }}>
              <button 
                onClick={() => setShowPremiumModal(true)} 
                style={{ 
                  padding: '0.6rem 1.5rem', 
                  background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)', 
                  color: '#FFFFFF', 
                  border: 'none', 
                  borderRadius: '0.75rem', 
                  fontWeight: '700', 
                  cursor: 'pointer', 
                  fontSize: '0.85rem', 
                  boxShadow: '0 4px 15px rgba(75,44,94,0.35)' 
                }}
              >
                ✨ Upgrade to send more messages
              </button>
            </div>
          )}

          {/* Input form */}
          <form 
            onSubmit={sendMessage} 
            style={{ 
              padding: '1rem 1.25rem', 
              borderTop: `1px solid ${colors.headerBorder}`, 
              background: colors.inputAreaBg 
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                className="comm-input"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={!isPremium && communityMsgsLeft <= 0 ? t('dailyLimitReached') : t('saySomething')}
                disabled={!isPremium && communityMsgsLeft <= 0}
                style={{
                  flex: 1, 
                  padding: '0.75rem 1rem',
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '0.75rem', 
                  fontSize: '0.95rem',
                  backgroundColor: colors.inputBg, 
                  color: colors.inputColor,
                  opacity: (!isPremium && communityMsgsLeft <= 0) ? 0.4 : 1,
                  boxSizing: 'border-box', 
                  transition: 'border-color 0.2s, box-shadow 0.2s'
                }}
              />
              <button 
                type="submit" 
                className="comm-send-btn"
                disabled={loading || !newMessage.trim() || (!isPremium && communityMsgsLeft <= 0)}
                style={{
                  padding: '0.75rem 1.25rem', 
                  background: 'linear-gradient(135deg, #008081, #005f5f)',
                  color: '#FFFFFF', 
                  borderRadius: '0.75rem', 
                  fontWeight: '700', 
                  border: 'none',
                  cursor: (loading || !newMessage.trim() || (!isPremium && communityMsgsLeft <= 0)) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !newMessage.trim() || (!isPremium && communityMsgsLeft <= 0)) ? 0.4 : 1,
                  flexShrink: 0, 
                  boxShadow: '0 4px 15px rgba(0,128,129,0.3)', 
                  fontSize: '0.9rem'
                }}
              >
                {t('send')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Premium Modal */}
      {showPremiumModal && (
        <PremiumModal
          onClose={() => setShowPremiumModal(false)}
          userEmail={user.email}
          userId={user.uid}
          theme={theme}
        />
      )}

      {/* Strike / Ban Warning Modal */}
      {strikeWarning && (
        <StrikeWarningModal
          strikes={strikeWarning.strikes}
          banned={strikeWarning.banned}
          theme={theme}
          onClose={() => setStrikeWarning(null)}
        />
      )}
    </>
  );
}